/**
 * The `Gauge` catalog component: one figure against named bands.
 *
 * Measured off screen 13d of the mobile redesign — "Is the market
 * overheated?", whose answer is "Warm, not hot. Three of four gauges are
 * fine." A reading says how a figure stands; a gauge says how far it is from
 * the edge, and that is the whole of what that screen is asked. Its anatomy
 * there is a 12px banded track, a 3px needle standing on it with the figure
 * written above, and a row of band labels under it, each as wide as its
 * band.
 *
 * NOTHING HERE SIZES THE TRACK. `share` and `at` are percentages the server
 * put on the wire, and a client that derived them from the band edges would
 * be choosing where "hot" begins — a claim about a market, made in geometry,
 * about figures nobody measured. The coldest band on 13d is "P/E under 18"
 * and has no floor at all, so there is nothing to derive from anyway.
 *
 * THE TRACK IS QUIET AND THE STATE IS THE COLOUR, which is the redesign's
 * palette rule read honestly. 13d paints COLD blue and HOT red; that mapping
 * is a judgement about which end is bad and it is not in the rows. What IS
 * in the rows is the needle's own `state`, the tool's word, so the band the
 * needle stands in takes the state's colour and every other band stays the
 * rule's. Green and red belong to the market and amber to Mitra, the same
 * rule `Readings` and `Withheld` follow, and `watch` is Mitra saying to look
 * again.
 *
 * Which band that is comes from the geometry — the running total of the
 * shares against `at` — which is a position and not a figure. Nothing about
 * the reading is decided here.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import { cellNumber, type CellValue } from '@/features/mitra/lib/figures'
import type { ReadingState } from '@/features/mitra/api/frames'
import { Fig } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const GaugeApi = {
  name: 'Gauge',
  schema: z
    .object({
      bands: PathBinding,
      needles: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shapes behind `/bands` and `/needles`, fixed by `to_messages`. */
export type BandPart = {
  id: string
  label: string
  share: CellValue
  from: CellValue
  to: CellValue
}
export type NeedlePart = {
  id: string
  at: CellValue
  value: CellValue
  state: ReadingState
}

/** The band a needle stands in, per state. Tokens, never hexes.
 *
 * `plain` is ink, not a signal. It is the state a row carries when the tool
 * measured a POSITION and passed no verdict on it — a price two thirds up its
 * 52-week range — and painting that green would be this file making the claim
 * the tool declined to. Green and red belong to the market, amber to Mitra,
 * and this to neither, so the lit band reads as "here" rather than as "good". */
const LIT: Record<string, string> = {
  fine: 'bg-up',
  watch: 'bg-caution',
  worse: 'bg-down',
  // `plain` LIGHTS NOTHING, and that is the state meaning what it says. A
  // range gauge has one band covering the whole track, so lighting the band
  // the needle stands in painted the entire bar a solid dark grey — a
  // progress bar, not a gauge, and a verdict colour on a needle that passes
  // none. The track stays the quiet rule and the needle carries the mark,
  // which is the only thing there is to say: here, on this range.
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const gaugeApi = GaugeApi as unknown as ComponentApi

export const Gauge = createComponentImplementation<ComponentApi>(
  gaugeApi,
  ({ props }: { props: { bands?: unknown; needles?: unknown; caption?: unknown } }) => (
    <GaugeTrack
      bands={listOf<BandPart>(props.bands)}
      needles={listOf<NeedlePart>(props.needles)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The drawing, given the lists the surface resolved. */
function GaugeTrack({
  bands,
  needles,
  caption,
}: {
  bands: BandPart[]
  needles: NeedlePart[]
  caption: string | null
}) {
  // A track with nothing on it is a coloured strip and a needle with no
  // track is a figure with a percentage beside it. The server declines to
  // compose either; this is the same refusal, because a renderer that drew
  // half a gauge would be drawing a different thing under the same name.
  if (bands.length === 0 || needles.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }

  const shares = bands.map((b) => Math.max(cellNumber(b.share), 0))
  const total = shares.reduce((a, b) => a + b, 0)
  // The bands are the axis, so their own shares are the scale. Falling back
  // to equal widths where they sum to nothing keeps a malformed track from
  // collapsing every band to zero and drawing the needle over an empty rule.
  const width = (i: number) => (total > 0 ? ((shares[i] ?? 0) / total) * 100 : 100 / bands.length)

  // Where each band ends, so a needle can say which one it stands in. Not a
  // figure and never drawn — a position, the way `Timeline` places a dot.
  const ends: number[] = []
  bands.forEach((_, i) => ends.push((ends[i - 1] ?? 0) + width(i)))
  const bandOf = (at: number) => {
    const found = ends.findIndex((end) => at <= end)
    return found === -1 ? bands.length - 1 : found
  }
  const litState: Record<number, ReadingState> = {}
  for (const needle of needles) {
    litState[bandOf(cellNumber(needle.at))] = needle.state
  }

  return (
    <figure
      data-role="chart"
      data-component="gauge"
      className="agent-gauge m-0 flex flex-col gap-2 px-3 pb-3 pt-6"
    >
      <div className="relative h-6">
        <div
          className="absolute inset-x-0 top-2 flex h-3 gap-0
                        overflow-hidden rounded-pill"
        >
          {bands.map((b, i) => (
            <span
              key={b.id}
              data-role="band"
              data-lit={litState[i] ?? ''}
              className={`block h-full ${
                // `bg-track` for a state with no colour, not a grey slab.
                // `ReadingState`'s own docstring is the rule — "a state with
                // no colour is a state the client would have to invent one
                // for" — so the honest answer is to invent none and leave the
                // track quiet. `plain` takes this path deliberately.
                LIT[litState[i] ?? ''] ?? 'bg-track'
              }`}
              style={{ width: `${String(width(i))}%` }}
            />
          ))}
        </div>
        {needles.map((n) => {
          const at = Math.min(Math.max(cellNumber(n.at), 0), 100)
          return (
            <div
              key={n.id}
              data-role="needle"
              data-state={n.state}
              className="absolute top-0 flex -translate-x-1/2 flex-col
                         items-center gap-0"
              style={{ left: `${String(at)}%` }}
            >
              <Fig cell={n.value} className="whitespace-nowrap text-mono-s tabular-nums" />
              <span className="block h-5 w-1 rounded-card bg-ink" />
            </div>
          )
        })}
      </div>
      <div data-role="gauge-axis-labels" className="flex gap-0">
        {bands.map((b, i) => (
          <span
            key={b.id}
            data-role="band-label"
            className="flex min-w-0 flex-col gap-0 font-mono
                       text-mono-s uppercase tracking-[0.04em] text-ink-soft"
            style={{ width: `${String(width(i))}%` }}
          >
            <span className="break-words leading-tight">{b.label}</span>
            {/* The edges as the rows carry them, with the missing side simply
                absent. 13d writes "P/E under 18" and "over 26"; those words
                would be this client inventing English for a band the server
                named, in a product whose other register is Devanagari. An
                en dash says the same thing in both. */}
            <span className="flex flex-wrap items-baseline gap-0 normal-case">
              {b.from != null && <Fig cell={b.from} className="text-mono-s tabular-nums" />}
              <span aria-hidden="true">–</span>
              {b.to != null && <Fig cell={b.to} className="text-mono-s tabular-nums" />}
            </span>
          </span>
        ))}
      </div>
      <ul data-role="gauge-legend" className="m-0 flex list-none flex-col gap-2 p-0">
        {bands.map((band, i) => (
          <li key={band.id} className="flex items-start gap-2 border-t border-rule pt-2">
            <span
              aria-hidden="true"
              className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
                LIT[litState[i] ?? ''] ?? 'bg-edge'
              }`}
            />
            <span className="min-w-0 flex-1 break-words text-body-xs text-ink-mid">
              {band.label}
            </span>
            <span className="shrink-0 whitespace-nowrap font-mono text-mono-s text-ink-soft">
              {band.from != null && <Fig cell={band.from} quietUnit />}
              <span aria-hidden="true"> – </span>
              {band.to != null && <Fig cell={band.to} quietUnit />}
            </span>
          </li>
        ))}
      </ul>
      {caption && (
        <figcaption data-role="caption" className="text-body-xs text-ink-soft">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}
